"use client";

import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { solutionLandings } from "@/lib/data/seo-landings";
import { services, featuredServices, industries, technologyGroups } from "@/lib/data/site";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function MegaMenu({ type }: { type: string }) {
  if (type === "services") {
    return (
      <div className="container-wide grid gap-8 py-10 lg:grid-cols-[1.45fr_0.85fr]">
        <div className="grid gap-2 sm:grid-cols-2">
          {featuredServices.map((service) => (
            <Link
              key={service.id}
              href={service.href}
              className="group relative overflow-hidden rounded-2xl border border-transparent p-4 transition hover:border-accent/30 hover:bg-card"
            >
              <div
                className={cn(
                  "pointer-events-none absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-gradient-to-r transition duration-300 group-hover:scale-x-100",
                  service.accent,
                )}
              />
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="font-mono text-xs text-accent">{service.number}</span>
                  <span className="text-sm font-medium leading-snug">{service.title}</span>
                </div>
                <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted opacity-0 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
              </div>
              <p className="text-sm leading-relaxed text-muted">{service.short}</p>
            </Link>
          ))}
        </div>
        <div className="rounded-3xl border border-border bg-surface p-6">
          <p className="eyebrow mb-4">Also in demand</p>
          <ul className="space-y-1 text-sm">
            {services
              .filter((s) => !s.featured)
              .slice(0, 10)
              .map((service) => (
                <li key={service.id}>
                  <Link
                    href={service.href}
                    className="flex items-center justify-between gap-2 rounded-xl px-2 py-2 text-muted transition hover:bg-card hover:text-foreground"
                  >
                    <span className="truncate">{service.title}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 opacity-40" />
                  </Link>
                </li>
              ))}
          </ul>
          <div className="mt-6">
            <Button href="/services" size="sm" variant="outline" arrow cursorLabel="EXPLORE">
              All services
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (type === "solutions") {
    return (
      <div className="container-wide grid gap-4 py-10 sm:grid-cols-2 lg:grid-cols-3">
        {solutionLandings.map((item) => (
          <Link
            key={item.slug}
            href={`/solutions/${item.slug}`}
            className="rounded-2xl border border-border p-5 transition hover:border-accent/40 hover:bg-card"
          >
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-medium">{item.title}</h3>
              <ArrowUpRight className="h-4 w-4 text-muted" />
            </div>
            <p className="text-sm text-muted">{item.description}</p>
          </Link>
        ))}
      </div>
    );
  }

  if (type === "industries") {
    return (
      <div className="container-wide grid gap-3 py-10 sm:grid-cols-2 lg:grid-cols-3">
        {industries.map((industry) => (
          <Link
            key={industry.slug}
            href={`/industries/${industry.slug}`}
            className="rounded-2xl border border-transparent px-4 py-3 transition hover:border-border hover:bg-card"
          >
            <h3 className="text-sm font-medium">{industry.name}</h3>
            <p className="mt-1 text-sm text-muted">{industry.description}</p>
          </Link>
        ))}
      </div>
    );
  }

  if (type === "technology") {
    return (
      <div className="container-wide grid gap-6 py-10 lg:grid-cols-5">
        {technologyGroups.map((group) => (
          <div key={group.category}>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted">
              {group.category}
            </p>
            <ul className="space-y-2">
              {group.items.map((item) => (
                <li key={item}>
                  <Link href="/technology" className="text-sm text-foreground/80 hover:text-accent">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  return null;
}